import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { useNotifications } from '../app/notifications.js';
import { Button } from './ui.js';

function BellIcon(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-[19px]"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1l1.5-2V9a7.5 7.5 0 1 1 15 0v4Z" />
      <path d="M9.5 19a2.5 2.5 0 0 0 5 0" />
    </svg>
  );
}

function ago(iso: string): string {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${String(Math.floor(seconds / 60))}m ago`;
  if (seconds < 86_400) return `${String(Math.floor(seconds / 3600))}h ago`;
  return `${String(Math.floor(seconds / 86_400))}d ago`;
}

export function NotificationBell(): ReactNode {
  const { items, unread, markRead, markAllRead } = useNotifications();
  const navigate = useNavigate();

  return (
    <Popover className="contents">
      <PopoverButton
        aria-label={unread === 0 ? 'Notifications' : `Notifications, ${String(unread)} unread`}
        className="relative grid size-9 place-items-center rounded-field text-navy transition hover:bg-navy/6 dark:text-dark-ink dark:hover:bg-white/10"
      >
        <BellIcon />
        {unread === 0 ? null : (
          <span className="absolute top-1 right-1 grid min-w-[17px] place-items-center rounded-full bg-critical px-1 text-[10px] font-bold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </PopoverButton>

      <PopoverPanel
        anchor="bottom end"
        className="z-50 flex w-[min(22rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-panel border border-hairline bg-white shadow-menu [--anchor-gap:8px] [--anchor-max-height:26rem] [--anchor-padding:12px] dark:border-dark-line dark:bg-dark-panel"
      >
        <div className="flex shrink-0 items-center gap-2 border-b border-hairline px-4 py-2.5 dark:border-dark-line">
          <span className="text-[13px] font-bold text-navy dark:text-dark-ink">Notifications</span>
          {unread === 0 ? null : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-auto"
              onClick={() => {
                void markAllRead();
              }}
            >
              Mark all read
            </Button>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-4 py-8 text-center text-[13px] text-muted">Nothing yet.</p>
          ) : (
            items.map((entry) => (
              <button
                key={entry.id}
                type="button"
                onClick={() => {
                  if (entry.readAt === null) void markRead(entry.id);
                  void navigate(`/tickets/${entry.ticketId}`);
                }}
                className={`flex w-full gap-2.5 border-b border-hairline px-4 py-3 text-left transition last:border-0 hover:bg-mist dark:border-dark-line dark:hover:bg-white/5 ${
                  entry.readAt === null ? '' : 'opacity-60'
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`mt-1.5 size-2 shrink-0 rounded-full ${
                    entry.readAt === null ? 'bg-royal' : 'bg-transparent'
                  }`}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-semibold text-navy dark:text-dark-ink">
                    {entry.title}
                  </span>
                  <span className="block truncate text-[12.5px] text-muted">{entry.body}</span>
                  <span className="mt-0.5 block text-[11.5px] text-muted">
                    {ago(entry.createdAt)}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      </PopoverPanel>
    </Popover>
  );
}
